// Fixed utils/index.ts - Clean version without duplication
import isPlainObject from 'is-plain-object';

// Keep your existing mobile detection
export const isMobile = (() => {
  if (typeof navigator === 'undefined' || typeof navigator.userAgent !== 'string') {
    return false;
  }
  return /Mobile/.test(navigator.userAgent);
})();

// Keep your existing removeUndefineds function
export function removeUndefineds<T>(obj: T): T {
  if (!isPlainObject(obj)) return obj;

  const target: { [name: string]: any } = {};

  for (const key in obj) {
    const val = obj[key];
    if (typeof val !== 'undefined') {
      target[key] = removeUndefineds(val);
    }
  }

  return target as T;
}

// Browser detection utility (shared by both files)
export const getBrowserInfo = () => {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor || '';

  return {
    isChrome: userAgent.includes('Chrome') && vendor.includes('Google') && !userAgent.includes('Edg'),
    isFirefox: userAgent.includes('Firefox'),
    isSafari: userAgent.includes('Safari') && vendor.includes('Apple') && !userAgent.includes('Chrome'),
    isEdge: userAgent.includes('Edg'),
    isMobile: /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent),
    userAgent,
    vendor,
  };
};

// Enhanced getDeviceInfo - REPLACES your existing one
export async function getDeviceInfo() {
  const { isChrome, isFirefox, isSafari } = getBrowserInfo();

  try {
    // Safari needs special handling
    if (isSafari) {
      try {
        const permissionStatus = await navigator.permissions?.query({ name: 'camera' as PermissionName });
        if (permissionStatus?.state === 'denied') {
          console.log('🍎 Safari: Camera permission denied');
        }
      } catch (e) {
        console.log('🍎 Safari: Permission query not supported');
      }
    }

    // Get devices
    const devices = await navigator.mediaDevices.enumerateDevices();

    // Filter devices by type
    const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
    const videoInputDevices = devices.filter(device => device.kind === 'videoinput');
    const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');

    // Browser-specific validation
    let hasAudioInputDevices = audioInputDevices.length > 0;
    let hasVideoInputDevices = videoInputDevices.length > 0;

    // Firefox and Safari sometimes show devices with empty labels when permission is denied
    if (isFirefox || isSafari) {
      const hasUnlabeledDevices = devices.some(device => !device.label && device.deviceId);
      if (hasUnlabeledDevices) {
        console.log(`🦊🍎 ${isFirefox ? 'Firefox' : 'Safari'}: Detected unlabeled devices`);
      }
    }

    // Chrome-specific checks
    if (isChrome) {
      const realAudioDevices = audioInputDevices.filter(device => device.deviceId !== 'default' && device.label !== '');
      const realVideoDevices = videoInputDevices.filter(device => device.deviceId !== 'default' && device.label !== '');

      if (realAudioDevices.length === 0 && audioInputDevices.length > 0) {
        hasAudioInputDevices = audioInputDevices.length > 0;
      }
      if (realVideoDevices.length === 0 && videoInputDevices.length > 0) {
        hasVideoInputDevices = videoInputDevices.length > 0;
      }
    }

    return {
      audioInputDevices,
      videoInputDevices,
      audioOutputDevices,
      hasAudioInputDevices,
      hasVideoInputDevices,
    };
  } catch (error) {
    console.error('❌ getDeviceInfo failed:', error);
    return {
      audioInputDevices: [],
      videoInputDevices: [],
      audioOutputDevices: [],
      hasAudioInputDevices: false,
      hasVideoInputDevices: false,
    };
  }
}

// Enhanced isPermissionDenied - REPLACES your existing one
export async function isPermissionDenied(name: PermissionName): Promise<boolean> {
  const { isSafari, isMobile, isFirefox } = getBrowserInfo();

  try {
    // Safari has limited support for permission queries
    if (isSafari) {
      console.log('🍎 Safari: Using fallback permission detection');
      return false; // Safari doesn't reliably support permission queries
    }

    // Mobile browsers have different permission handling
    if (isMobile) {
      console.log('📱 Mobile browser: Limited permission query support');
      return false;
    }

    // Standard permission API approach
    if ('permissions' in navigator && navigator.permissions.query) {
      try {
        const result = await navigator.permissions.query({ name });
        console.log(`🔐 Permission status for ${name}:`, result.state);
        return result.state === 'denied';
      } catch (error) {
        console.log(`❌ Permission query failed for ${name}:`, error);

        // Firefox sometimes fails permission queries
        if (isFirefox) {
          return await fallbackPermissionCheck(name);
        }

        return false;
      }
    }

    // Fallback for browsers without permission API
    return await fallbackPermissionCheck(name);
  } catch (error) {
    console.error(`❌ Permission check failed for ${name}:`, error);
    return false;
  }
}

// Fallback permission check helper
async function fallbackPermissionCheck(name: PermissionName): Promise<boolean> {
  try {
    const constraints: MediaStreamConstraints = {};

    if (name === 'camera') {
      constraints.video = true;
    } else if (name === 'microphone') {
      constraints.audio = true;
    } else {
      return false; // Unknown permission type
    }

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stream.getTracks().forEach(track => track.stop());
    return false; // Permission granted
  } catch (error) {
    const errorName = (error as any)?.name || '';
    return errorName === 'NotAllowedError';
  }
}

// Additional utility for media stream creation with retries
export const createMediaStream = async (
  constraints: MediaStreamConstraints,
  retries: number = 2
): Promise<MediaStream> => {
  const { isChrome, isFirefox, isSafari } = getBrowserInfo();

  let lastError: any;

  for (let attempt = 1; attempt <= retries + 1; attempt++) {
    try {
      console.log(`🎥 Attempt ${attempt} to create media stream`);

      // Browser-specific constraint adjustments
      let adjustedConstraints = { ...constraints };

      if (isSafari && attempt > 1) {
        // Safari sometimes needs simpler constraints on retry
        if (adjustedConstraints.video && typeof adjustedConstraints.video === 'object') {
          adjustedConstraints.video = true;
        }
        if (adjustedConstraints.audio && typeof adjustedConstraints.audio === 'object') {
          adjustedConstraints.audio = true;
        }
      }

      if (isFirefox && attempt > 1) {
        // Firefox sometimes needs different settings
        if (adjustedConstraints.audio && typeof adjustedConstraints.audio === 'object') {
          adjustedConstraints.audio = {
            ...adjustedConstraints.audio,
            echoCancellation: false,
          };
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia(adjustedConstraints);
      console.log(`✅ Successfully created media stream on attempt ${attempt}`);
      return stream;
    } catch (error) {
      lastError = error;
      console.log(`❌ Media stream creation failed on attempt ${attempt}:`, error);

      // Don't retry on permission errors
      if ((error as any)?.name === 'NotAllowedError') {
        break;
      }

      // Add delay between retries
      if (attempt <= retries) {
        await new Promise(resolve => setTimeout(resolve, 500 * attempt));
      }
    }
  }

  throw lastError;
};
